import { useMemo } from "react";
import classNames from "classnames";

import { MessagesConversation } from "slices/messages";

import PetNamePill from "../../components/PetNamePill";
import DateItem from "../../components/DateItem";

type ConversationListItemProps = {
  conversation: Partial<MessagesConversation>;
  isSelected?: boolean;
  draft?: boolean;
  onClick?: () => void;
  "data-test-id"?: string;
};

const UnreadDot = ({
  "data-test-id": dataTestId,
}: {
  "data-test-id"?: string;
}): JSX.Element => (
  <div
    className="MessageCard_Unread-Dot mr-2 mb-1 bg-light rounded-full shrink-0 w-[10px] h-[10px]"
    data-test-id={dataTestId}
  />
);

const ConversationListItemCard = ({
  onClick,
  isSelected,
  draft,
  conversation,
  "data-test-id": dataTestId,
}: ConversationListItemProps): JSX.Element => {
  const lastMessage = useMemo(
    () =>
      conversation.messages
        ? conversation.messages[conversation.messages.length - 1]
        : undefined,
    [conversation.messages],
  );

  return (
    <div
      onClick={onClick}
      className={classNames(
        "p-4 mb-4 border cursor-pointer transition-all duration-500",
        {
          "border-grey-light bg-grey-lightest": !isSelected,
          "border-light bg-lightest": isSelected,
        },
      )}
      aria-selected={isSelected ? "true" : "false"}
      data-test-id={dataTestId}
    >
      <div className="flex flex-row justify-between items-center">
        <p
          className="pr-4 overflow-hidden text-ellipsis whitespace-nowrap"
          data-test-id={`${dataTestId}_Title`}
        >
          {draft && !conversation.summary?.title
            ? "New Conversation"
            : conversation.summary?.title}
        </p>
        <DateItem
          date={conversation.lastUpdated}
          data-test-id={`${dataTestId}_Date`}
        />
      </div>
      {conversation.summary?.pet?.map((pet) => (
        <PetNamePill
          key={pet.id}
          petName={pet.name}
          isSelected={isSelected}
          data-test-id={`${dataTestId}_PetName`}
        />
      )) || <PetNamePill />}
      <div className="flex flex-row items-center">
        {Boolean(conversation.numUnread) && (
          <UnreadDot data-test-id={`${dataTestId}_UnreadDot`} />
        )}
        <p
          className="text-sm overflow-hidden text-ellipsis whitespace-nowrap"
          data-test-id={`${dataTestId}_LastMessage`}
        >
          {lastMessage ? lastMessage.message : "No messages"}
        </p>
      </div>
    </div>
  );
};

export default ConversationListItemCard;
